/**
 * WHICH ROLES GET A ROW.
 *
 * The board's year is mostly retrospective, and whether a filled seat reads as
 * "this year's hire" or as "somebody who was already here" is not a fact about
 * the person — it is a fact about the span you are asking over. Daniel Wranik is
 * a 2026 hire on a year board and a standing colleague on a T3 board, and both
 * are true. So the span is a control rather than a rule.
 *
 * Theresa, Sep 2026: "which role gets a hiring view where we can see the start
 * date, did we fill it in time — and which role exists as: this is a current
 * employee."
 *
 * A role is in the window when its EFFECTIVE month is: the actual start if
 * somebody began, else the adjusted plan, else the original — the same
 * precedence the server uses to decide where a row sits on the timeline.
 *
 * Deliberately NOT a time machine. An earlier draft had a second control for
 * "as of" date; Theresa: "today is today. I don't want to go back in time." The
 * window moves what you are looking BACK OVER, never what counts as now.
 */
import type { BoardRole, DemandBoard, HireState } from '../../../types/taDemand';
import { isDraft } from '../../../types/taDemand';

export type WindowKind = 'year' | 'trimester' | 'custom';

export interface DemandWindow {
  kind: WindowKind;
  /** Inclusive `YYYY-MM` bounds. */
  from: string;
  to: string;
}

/** FINN trimesters are four months: T1 Jan–Apr, T2 May–Aug, T3 Sep–Dec. */
export const TRIMESTERS = [1, 2, 3] as const;

const pad = (n: number) => String(n).padStart(2, '0');

export function trimesterWindow(year: number, t: number): DemandWindow {
  const start = (t - 1) * 4 + 1;
  return { kind: 'trimester', from: `${year}-${pad(start)}`, to: `${year}-${pad(start + 3)}` };
}

export const yearWindow = (year: number): DemandWindow => ({
  kind: 'year',
  from: `${year}-01`,
  to: `${year}-12`,
});

/** The period the board was opened for, e.g. `t3-2026`, as a starting window. */
export function defaultWindow(period: string): DemandWindow {
  const m = /^t([123])-(\d{4})$/i.exec(period.trim());
  const year = m ? Number(m[2]) : new Date().getFullYear();
  return yearWindow(year);
}

/**
 * Where a role SITS. Same precedence as the server's `month`: the truth if there
 * is one, else the current expectation, else the original commitment.
 */
export const effectiveMonth = (r: BoardRole): string =>
  (isDraft(r) ? r.month : r.actualMonth || r.adjustedMonth || r.plannedMonth || r.month) || '';

/**
 * An UNDATED role has no place on the timeline but is still real demand — it is
 * kept in every window rather than vanishing into whichever one nobody looks at.
 */
export const inWindow = (r: BoardRole, w: DemandWindow): boolean => {
  const m = effectiveMonth(r);
  if (!m) return true;
  return m >= w.from && m <= w.to;
};

/**
 * The board, narrowed.
 *
 * The roster is re-derived rather than trusted: the server marked a person as
 * living on a role row, but if the window no longer shows that row they belong
 * back in the current team. Otherwise narrowing to a trimester would make
 * everyone hired earlier in the year disappear from the board entirely.
 */
export function applyWindow(board: DemandBoard, w: DemandWindow): DemandBoard {
  const kept = board.hdb.filter((r) => inWindow(r, w));
  const drafts = board.drafts.filter((r) => inWindow(r, w));
  // Cancelled roles narrow with everything else: a seat dropped in March is not
  // part of a T3 story, and the collapsed list is only useful while it is short.
  const cancelled = (board.cancelled ?? []).filter((r) => inWindow(r, w));
  const shown = new Set<string>([...kept, ...drafts].map((r) => r.id));

  /*
    Somebody hired twice may have their LATEST role fall outside the window while
    an earlier one stays. Their seat in this window is that earlier row, so it is
    promoted out of `superseded` and they stay off the current team. Taking only
    `onRoleRow` at face value put the colleague back in the team beside their own
    still-visible role row — one person drawn twice, FTE counted twice with them.
  */
  const promoted = new Map<string, string>();
  const roster = (board.roster ?? []).map((m) => {
    if (!m.onRoleRow) return m;
    if (shown.has(m.onRoleRow)) return m;
    const survivor = (m.onRoleLinks ?? []).find((id) => shown.has(id));
    if (!survivor) return { ...m, onRoleRow: undefined };
    promoted.set(survivor, m.onRoleRow);
    return { ...m, onRoleRow: survivor };
  });

  const hdb = kept.map((r) =>
    promoted.has(r.id) && !isDraft(r) && r.hire
      ? { ...r, hireState: stateOf(r.hire.started, r.hire.startDate) }
      : r,
  );
  return { ...board, hdb, drafts, cancelled, roster };
}

/** The same three-way call the server makes, for a row promoted client-side. */
const stateOf = (started: boolean, startDate: string | null): HireState =>
  startDate === null ? 'undated' : started ? 'landed' : 'incoming';

export const windowLabel = (w: DemandWindow): string => {
  if (w.kind === 'year') return w.from.slice(0, 4);
  const [y, mo] = w.from.split('-');
  if (w.kind === 'trimester') return `T${Math.floor((Number(mo) - 1) / 4) + 1} ${y}`;
  return `${w.from} — ${w.to}`;
};
